// General settings tab — Profile (avatar, full name, preferred name, describes) + Preferences
// (Instructions / Appearance, both disabled "coming soon"). Edits save on blur via the shell.

import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Avatar from '@mui/material/Avatar'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { getAvailableMascots, getMascotByName } from '@reachinsight-app/shared/src/components/R3Chat/RosMascots'
import type { UserSettings, UserSettingsPatch } from '../pages/userSettingsApi'
import { SERIF_HEADING } from '../theme'

// One labelled row: left-aligned label + description, right-aligned control (the Claude layout).
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, py: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ flexShrink: 0, maxWidth: 320 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</Typography>
        {hint && <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>{hint}</Typography>}
      </Box>
      <Box sx={{ flex: 1, maxWidth: 420, display: 'flex', justifyContent: 'flex-end' }}>{children}</Box>
    </Box>
  )
}

/** Render the current avatar (image > mascot > initials), at a given size. */
export function ResolvedAvatar({ settings, fallbackName, size = 56 }: { settings: UserSettings | null; fallbackName?: string; size?: number }) {
  if (settings?.avatarType === 'image' && settings.avatarUrl) {
    return <Avatar src={settings.avatarUrl} sx={{ width: size, height: size }} />
  }
  if (settings?.avatarType === 'mascot' && settings.avatarMascot) {
    const M = getMascotByName(settings.avatarMascot).avatarPlain
    return <Box sx={{ width: size, height: size, flexShrink: 0 }}><M size={size} /></Box>
  }
  const initials = (fallbackName || '?').trim().slice(0, 2).toUpperCase()
  return <Avatar sx={{ width: size, height: size, bgcolor: 'primary.main', fontSize: size * 0.36 }}>{initials}</Avatar>
}

export function SettingsGeneral({
  settings, onSave, onUploadAvatar, onFindLocation, onResolveManualLocation, onRemoveLocation, showLocation,
}: {
  settings: UserSettings | null
  onSave: (patch: UserSettingsPatch) => Promise<UserSettings>
  onUploadAvatar: (file: File) => Promise<void>
  onFindLocation: () => void
  onResolveManualLocation: (value: string) => Promise<void>
  onRemoveLocation: () => Promise<void>
  showLocation: boolean
}) {
  const [fullName, setFullName] = useState('')
  const [preferredName, setPreferredName] = useState('')
  const [describes, setDescribes] = useState('')
  const [manualLocation, setManualLocation] = useState('')
  const [avatarAnchor, setAvatarAnchor] = useState<null | HTMLElement>(null)
  // Persistent save status shown in the header (not a fleeting toast): green on success, red on error.
  // It stays until the next save, so a quiet auto-save still leaves a clear, lasting confirmation.
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null)
  const ok = (msg: string) => setStatus({ kind: 'ok', msg })
  const err = (msg: string) => setStatus({ kind: 'error', msg })
  const fileRef = useRef<HTMLInputElement>(null)

  // Seed local fields once settings load (and keep in sync if they change underneath).
  useEffect(() => {
    setFullName(settings?.fullName ?? '')
    setPreferredName(settings?.preferredName ?? '')
    setDescribes(settings?.describes ?? '')
    setManualLocation(settings?.locationDisplayName ?? '')
  }, [settings?.fullName, settings?.preferredName, settings?.describes, settings?.locationDisplayName])

  const saveField = async (patch: UserSettingsPatch) => {
    try { await onSave(patch); ok('Saved') } catch { err('Could not save — try again.') }
  }

  const pickMascot = async (id: string) => {
    setAvatarAnchor(null)
    try { await onSave({ avatarType: 'mascot', avatarMascot: id }); ok('Avatar updated') }
    catch { err('Could not update avatar.') }
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''  // allow re-selecting the same file
    if (!file) return
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { err('Use a PNG, JPEG, or WebP image.'); return }
    if (file.size > 2 * 1024 * 1024) { err('Image must be under 2MB.'); return }
    try { await onUploadAvatar(file); ok('Avatar uploaded') }
    catch (e2) { err(e2 instanceof Error ? e2.message : 'Upload failed.') }
  }

  const mascots = getAvailableMascots()
  const locationLabel = settings?.locationDisplayName || 'No location saved'

  const resolveManualLocation = async () => {
    const value = manualLocation.trim()
    if (!value) return
    try { await onResolveManualLocation(value) }
    catch (error) { err(error instanceof Error ? error.message : 'Could not resolve location.') }
  }

  const removeLocation = async () => {
    try { await onRemoveLocation(); ok('Saved location removed') }
    catch { err('Could not remove location.') }
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 5, py: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
        <Typography variant="h5" sx={SERIF_HEADING}>Profile</Typography>
        {status && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.8rem', fontWeight: 600, color: status.kind === 'ok' ? 'success.main' : 'error.main' }}>
            {status.kind === 'ok' && <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            {status.msg}
          </Box>
        )}
      </Box>

      <Row label="Avatar" hint="Pick a mascot or upload your own. This shows in the sidebar.">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ResolvedAvatar settings={settings} fallbackName={fullName || preferredName} size={56} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Button size="small" variant="outlined" onClick={e => setAvatarAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>Choose prebuilt</Button>
            <Button size="small" startIcon={<UploadOutlinedIcon />} onClick={() => fileRef.current?.click()} sx={{ textTransform: 'none' }}>Upload image</Button>
          </Box>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onFile} />
        </Box>
      </Row>

      <Row label="Full name" hint="Used to greet you on the home screen.">
        <TextField fullWidth size="small" value={fullName} onChange={e => setFullName(e.target.value)}
          onBlur={() => { if (fullName !== (settings?.fullName ?? '')) void saveField({ fullName: fullName.trim() || null }) }} />

      </Row>

      <Row label="What should Ros call you?" hint="Ros will address you by this name.">
        <TextField fullWidth size="small" value={preferredName} onChange={e => setPreferredName(e.target.value)}
          onBlur={() => { if (preferredName !== (settings?.preferredName ?? '')) void saveField({ preferredName: preferredName.trim() || null }) }} />

      </Row>

      <Row label="What best describes you?" hint="A line about your work — helps Ros tailor its help.">
        <TextField fullWidth size="small" value={describes} onChange={e => setDescribes(e.target.value)}
          placeholder="e.g. Researcher focused on CPG brand tracking"
          onBlur={() => { if (describes !== (settings?.describes ?? '')) void saveField({ describes: describes.trim() || null }) }} />

      </Row>

      {/* Full-width stacked row (label on top, full-width textarea below) — a multiline field doesn't
          belong in the right-aligned narrow column the single-line fields use. */}
      <Box sx={{ py: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Instructions for Ros</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25, mb: 1 }}>Ros will keep these in mind across chats. Coming soon.</Typography>
        <TextField fullWidth multiline minRows={3} size="small" disabled placeholder="e.g. keep explanations brief and to the point" />
      </Box>

      <Typography variant="h5" sx={{ ...SERIF_HEADING, mt: 4, mb: 1 }}>Preferences</Typography>
      {showLocation && (
        <Row label="Location" hint="Used only when your question needs local context.">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.75, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 600 }}>{locationLabel}</Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={manualLocation}
              onChange={event => setManualLocation(event.target.value)}
              placeholder="Enter a city or municipality"
              onKeyDown={event => {
                if (event.key === 'Enter') void resolveManualLocation()
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button size="small" variant="contained" disabled={!manualLocation.trim()} onClick={() => void resolveManualLocation()} sx={{ textTransform: 'none' }}>
                Review &amp; save
              </Button>
              <Button size="small" variant="outlined" onClick={onFindLocation} sx={{ textTransform: 'none' }}>
                Find my location
              </Button>
              {!!settings?.locationConfirmedAt && (
                <Button size="small" color="inherit" onClick={() => void removeLocation()} sx={{ textTransform: 'none' }}>Remove</Button>
              )}
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', maxWidth: 360 }}>
              Ros always shows the readable location for your confirmation before saving it.
            </Typography>
          </Box>
        </Row>
      )}
      <Row label="Appearance" hint="Theme. Coming soon.">
        <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none' }}>System</Button>
      </Row>

      <Menu anchorEl={avatarAnchor} open={!!avatarAnchor} onClose={() => setAvatarAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 360 } } }}>
        {mascots.map(m => {
          const M = getMascotByName(m.id).avatarPlain
          return (
            <MenuItem key={m.id} onClick={() => void pickMascot(m.id)} sx={{ gap: 1.25 }}>
              <Box sx={{ width: 28, height: 28, flexShrink: 0 }}><M size={28} /></Box>
              <Typography sx={{ fontSize: '0.85rem' }}>{m.name}</Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}
